import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TerminatedStaffPage.css';
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';
import { COMPANIES, DEPARTMENTS } from '../../../utils/companyData';


const TerminatedStaffPage = () => {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    role: '',
    department: '',
    terminationDate: '',
    reason: '',
    details: '',
    lastWorkingDay: '',
    noticePeriodServed: false,
    exitInterview: false,
  });
  const [terminatedStaff, setTerminatedStaff] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const API_URL = `${API_CONFIG.BASE_URL}/api`;

  // Helper function to get department name from ID
  const getDepartmentName = (departmentId) => {
    if (!departmentId) return 'N/A';
    const department = departments.find(dept => dept._id === departmentId || dept.id === departmentId);
    return department ? department.name : departmentId; // Fallback to ID if name not found
  };

  // Helper function to format termination date
  const formatTerminationDate = (date) => {
    if (!date) return 'N/A';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return 'Invalid Date';
      return dateObj.toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };


  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!currentUser) {
          return;
        }

        setLoading(true);
        setError('');

        const idToken = await currentUser.getIdToken(true);
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        };

         // Try to fetch terminated staff data
         let terminatedData = [];
         try {
           console.log('Fetching terminated staff from:', `${API_URL}/terminated-staff`);
           const terminatedRes = await axios.get(`${API_URL}/terminated-staff`, { headers });
           console.log('Terminated staff API response:', terminatedRes);
           terminatedData = Array.isArray(terminatedRes?.data) ? terminatedRes.data : [];
           console.log('Processed terminated data:', terminatedData);
         } catch (terminatedErr) {
           console.error('Failed to fetch terminated staff:', terminatedErr);
           if (terminatedErr.response?.status === 404 || terminatedErr.message?.includes('404') || terminatedErr.message?.includes('Not Found')) {
             console.warn('Terminated staff endpoint not available - using empty array');
             terminatedData = [];
           } else {
             setError(`Failed to load terminated staff: ${terminatedErr.response?.data?.error || terminatedErr.message}`);
           }
         }

        // Try to fetch companies data
        let companyData = COMPANIES; // Default to hardcoded
        try {
          const companiesRes = await axios.get(`${API_URL}/companies`, { headers });
          const companyNames = Array.isArray(companiesRes.data) 
            ? companiesRes.data.map(company => ({
                _id: company._id || company.id,
                name: company.name || company.companyName || 'Unknown Company'
              }))
            : Array.isArray(companiesRes.data?.companies)
            ? companiesRes.data.companies.map(company => ({
                _id: company._id || company.id,
                name: company.name || company.companyName || 'Unknown Company'
              }))
            : COMPANIES;
          companyData = companyNames;
        } catch (companiesErr) {
          console.error('Failed to fetch companies:', companiesErr);
          if (companiesErr.response?.status === 404 || companiesErr.message?.includes('404') || companiesErr.message?.includes('Not Found')) {
            console.warn('Companies endpoint not available - using hardcoded companies');
            companyData = COMPANIES;
          } else {
            setError(`Failed to load companies: ${companiesErr.response?.data?.error || companiesErr.message}`);
          }
        }

        // Try to fetch departments data
        let departmentData = DEPARTMENTS; // Default to hardcoded
        try {
          const departmentsRes = await axios.get(`${API_URL}/auth/all-departments`, { headers });
          departmentData = Array.isArray(departmentsRes?.data) ? departmentsRes.data : DEPARTMENTS;
        } catch (departmentsErr) {
          console.error('Failed to fetch departments:', departmentsErr);
          if (departmentsErr.response?.status === 404 || departmentsErr.message?.includes('404') || departmentsErr.message?.includes('Not Found')) {
            console.warn('Departments endpoint not available - using hardcoded departments');
            departmentData = DEPARTMENTS;
          } else {
            setError(`Failed to load departments: ${departmentsErr.response?.data?.error || departmentsErr.message}`);
          }
        }

        setAllCompanies(companyData);
        setTerminatedStaff(terminatedData);
        setDepartments(departmentData);
        
        // Clear any previous errors if we have fallback data
        if (companyData.length > 0 && departmentData.length > 0) {
          setError('');
        }
      } catch (err) {
        console.error('Unexpected error fetching data:', err);
        if (err.response?.status === 401) {
          navigate('/login');
        } else {
          setError(err.response?.data?.error || err.message || 'Failed to fetch data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInput = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleCreate = async () => {
    try {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      if (!formData.company || !formData.terminationDate || !formData.reason) {
        setError('Company, Termination Date, and Reason are required fields');
        return;
      }

      const terminationData = {
        company: formData.company,
        name: formData.name || undefined,
        role: formData.role || undefined,
        department: formData.department || undefined,
        terminationDate: formData.terminationDate,
        reason: formData.reason,
        details: formData.details,
        lastWorkingDay: formData.lastWorkingDay || undefined,
        noticePeriodServed: formData.noticePeriodServed,
        exitInterview: formData.exitInterview
      };

      const idToken = await currentUser.getIdToken(true);
      
      try {
        const response = await axios.post(`${API_URL}/terminated-staff`, terminationData, {
          headers: { 
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        });

        setTerminatedStaff(prev => [...prev, response.data]);
        resetForm();
        setError('');
      } catch (apiErr) {
        if (apiErr.response?.status === 404 || apiErr.message?.includes('404') || apiErr.message?.includes('Not Found')) {
          console.warn('Terminated staff endpoint not available - storing locally');
          // Store locally as fallback
          const localRecord = {
            _id: `local-${Date.now()}`,
            ...terminationData,
            createdAt: new Date().toISOString(),
            isLocal: true
          };
          
          setTerminatedStaff(prev => [...prev, localRecord]);
          resetForm();
          setError('Terminated staff record saved locally (API endpoint unavailable). Data will be synced when server is available.');
          setTimeout(() => setError(''), 3000);
        } else {
          throw apiErr;
        }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to create record');
      }
    }
  };

  const handleUpdate = async (id) => {
    try {
      console.log('Updating terminated staff with ID:', id);
      console.log('Current form data:', formData);
      
      if (!currentUser) {
        navigate('/login');
        return;
      }

      const updateData = {
        name: formData.name || undefined,
        role: formData.role || undefined,
        department: formData.department || undefined,
        terminationDate: formData.terminationDate || undefined,
        reason: formData.reason,
        details: formData.details,
        lastWorkingDay: formData.lastWorkingDay || undefined,
        noticePeriodServed: formData.noticePeriodServed,
        exitInterview: formData.exitInterview
      };
      
      console.log('Update data being sent:', updateData);

      const idToken = await currentUser.getIdToken(true);
      const response = await axios.put(
        `${API_URL}/terminated-staff/${id}`,
        updateData,
        { 
          headers: { 
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          } 
        }
      );

      console.log('Update successful:', response.data);
      setTerminatedStaff(prev => 
        prev.map(item => (item._id === id ? response.data : item))
      );
      resetForm();
      setError('');
    } catch (err) {
      console.error('Update failed:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/login');
      } else {
        setError(err.response?.data?.error || 'Failed to update record');
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      if (!id) {
        console.error('Cannot delete: Staff ID is undefined');
        setError('Cannot delete: Staff ID is missing');
        return;
      }

      if (!window.confirm('Are you sure you want to delete this terminated staff record?')) {
        return;
      }
      
      const idToken = await currentUser.getIdToken(true);
      
      try {
        await axios.delete(`${API_URL}/terminated-staff/${id}`, {
          headers: { 
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        setTerminatedStaff(prev => prev.filter(item => item._id !== id));
        setError('');
      } catch (apiErr) {
        if (apiErr.response?.status === 404 || apiErr.message?.includes('404') || apiErr.message?.includes('Not Found')) {
          console.warn('Terminated staff endpoint not available - removing from local list');
          // Remove from local list
          setTerminatedStaff(prev => prev.filter(item => item._id !== id));
          setError('Record removed from local list (API endpoint unavailable).');
          setTimeout(() => setError(''), 3000);
        } else {
          throw apiErr;
        }
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/login');
      } else {
        setError(err.response?.data?.error || 'Failed to delete record');
      }
    }
  };

  // Helper function to safely format date strings
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    if (typeof dateValue === 'string') {
      return dateValue.split('T')[0];
    }
    if (dateValue instanceof Date) {
      return dateValue.toISOString().split('T')[0];
    }
    return '';
  };

  const handleEdit = (staff) => {
    console.log('Editing staff:', staff);
    console.log('Staff ID:', staff._id || staff.id);
    
    setEditingId(staff._id || staff.id);
    setFormData({
      company: staff.company?._id || staff.company || '',
      name: staff.name || '',
      role: staff.role || '',
      department: staff.department || '',
      terminationDate: formatDateForInput(staff.terminationDate),
      reason: staff.reason || '',
      details: staff.details || '',
      lastWorkingDay: formatDateForInput(staff.lastWorkingDay),
      noticePeriodServed: staff.noticePeriodServed || false,
      exitInterview: staff.exitInterview || false,
    });
    setError('');
    
    console.log('Form data set for editing:', {
      editingId: staff._id || staff.id,
      formData: {
        company: staff.company?._id || staff.company || '',
        name: staff.name || '',
        role: staff.role || '',
        department: staff.department || '',
        terminationDate: formatDateForInput(staff.terminationDate),
        reason: staff.reason || '',
        details: staff.details || '',
        lastWorkingDay: formatDateForInput(staff.lastWorkingDay),
        noticePeriodServed: staff.noticePeriodServed || false,
        exitInterview: staff.exitInterview || false,
      }
    });
  };

  const resetForm = () => {
    setFormData({
      company: '',
      name: '',
      role: '',
      department: '',
      terminationDate: '',
      reason: '',
      details: '',
      lastWorkingDay: '',
      noticePeriodServed: false,
      exitInterview: false,
    });
    setEditingId(null);
    setError('');
  };


  // Print function
  const handlePrint = () => {
    // Create a comprehensive print view with proper heading
    const printContent = `
      <html>
        <head>
          <title>Terminated Staff Records Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              background: white;
              color: #333;
            }
            .print-header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 3px solid #dc3545;
              padding-bottom: 20px;
            }
            .print-title {
              font-size: 24px;
              font-weight: bold;
              color: #dc3545;
              margin-bottom: 10px;
            }
            .print-subtitle {
              font-size: 16px;
              color: #666;
              margin-bottom: 5px;
            }
            .print-date {
              font-size: 14px;
              color: #888;
            }
            .stats-section {
              margin: 20px 0;
              padding: 15px;
              background-color: #f8f9fa;
              border-radius: 5px;
            }
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 15px;
              margin-bottom: 20px;
            }
            .stat-item {
              text-align: center;
              padding: 10px;
              background: white;
              border-radius: 5px;
              border: 1px solid #dee2e6;
            }
            .stat-number {
              font-size: 24px;
              font-weight: bold;
              color: #dc3545;
            }
            .stat-label {
              font-size: 12px;
              color: #666;
              margin-top: 5px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 20px 0;
              font-size: 12px;
            }
            th, td { 
              border: 1px solid #000; 
              padding: 8px; 
              text-align: left;
            }
            th { 
              background-color: #dc3545; 
              color: white;
              font-weight: bold; 
              text-align: center;
            }
            .present-row {
              background-color: #f8f9fa;
            }
            .absent-row {
              background-color: #fff5f5;
            }
            .status-present {
              color: #28a745;
              font-weight: bold;
            }
            .status-absent {
              color: #dc3545;
              font-weight: bold;
            }
            .role-badge {
              display: inline-block;
              padding: 2px 6px;
              border-radius: 3px;
              font-size: 10px;
              font-weight: bold;
              text-transform: uppercase;
            }
            .role-admin {
              background-color: #ffc107;
              color: #000;
            }
            .role-staff {
              background-color: #007bff;
              color: white;
            }
            .role-super_admin {
              background-color: #dc3545;
              color: white;
            }
            .print-footer {
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #dee2e6;
              font-size: 12px;
              color: #666;
            }
            .no-print {
              display: none !important;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="print-title">Terminated Staff Records Report</div>
            <div class="print-subtitle">Staff Termination Management System</div>
            <div class="print-date">Generated: ${new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })} at ${new Date().toLocaleTimeString()}</div>
          </div>

          <div class="stats-section">
            <h3 style="margin-top: 0; color: #dc3545;">Termination Summary</h3>
            <div class="stats-grid">
              <div class="stat-item">
                <div class="stat-number">${terminatedStaff.length}</div>
                <div class="stat-label">Total Terminations</div>
              </div>
              <div class="stat-item">
                <div class="stat-number" style="color: #28a745;">${terminatedStaff.filter(staff => staff.noticePeriodServed).length}</div>
                <div class="stat-label">Notice Period Served</div>
              </div>
              <div class="stat-item">
                <div class="stat-number" style="color: #007bff;">${terminatedStaff.filter(staff => staff.exitInterview).length}</div>
                <div class="stat-label">Exit Interviews</div>
              </div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Staff Name</th>
                <th>Role</th>
                <th>Department</th>
                <th>Termination Date</th>
                <th>Reason</th>
                <th>Notice Served</th>
                <th>Exit Interview</th>
                <th>Last Working Day</th>
              </tr>
            </thead>
            <tbody>
              ${terminatedStaff.length > 0 ? 
                terminatedStaff.map((staff, index) => `
                  <tr class="${index % 2 === 0 ? 'present-row' : 'absent-row'}">
                    <td><strong>${staff.name || 'N/A'}</strong></td>
                    <td>
                      <span class="role-badge role-${staff.role?.toLowerCase().replace(' ', '_')}">
                        ${staff.role || 'N/A'}
                      </span>
                    </td>
                    <td>${getDepartmentName(staff.department)}</td>
                    <td>${formatTerminationDate(staff.terminationDate)}</td>
                    <td><strong>${staff.reason || 'N/A'}</strong></td>
                    <td class="${staff.noticePeriodServed ? 'status-present' : 'status-absent'}">
                      ${staff.noticePeriodServed ? '✓ Yes' : '✗ No'}
                    </td>
                    <td class="${staff.exitInterview ? 'status-present' : 'status-absent'}">
                      ${staff.exitInterview ? '✓ Yes' : '✗ No'}
                    </td>
                    <td>${staff.lastWorkingDay ? new Date(staff.lastWorkingDay).toLocaleDateString() : 'N/A'}</td>
                  </tr>
                `).join('') : 
                `<tr><td colspan="8" style="text-align: center; padding: 20px; color: #666;">No terminated staff records found</td></tr>`
              }
            </tbody>
          </table>

          <div class="print-footer">
            <p><strong>Report Generated:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Total Terminations:</strong> ${terminatedStaff.length}</p>
            <p><strong>Notice Period Served:</strong> ${terminatedStaff.filter(staff => staff.noticePeriodServed).length}</p>
            <p><strong>Exit Interviews Conducted:</strong> ${terminatedStaff.filter(staff => staff.exitInterview).length}</p>
            <p style="margin-top: 10px; font-size: 10px; color: #999;">
              This report shows all terminated staff records from the HR Management System.
            </p>
          </div>
        </body>
      </html>
    `;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Download as PDF function
  const handleDownload = () => {
    const table = document.querySelector('.records-table');
    if (!table) {
      alert('No table found to download');
      return;
    }

    // Clone the table and remove all interactive elements
    const tableClone = table.cloneNode(true);
    
    // Remove ALL interactive elements more comprehensively
    const interactiveSelectors = [
      'button', 'input', 'select', 'textarea',
      '[onclick]', '[onchange]', '[oninput]', '[onmousedown]', '[onmouseup]',
      '.btn', '.button', '.form-control', '.form-select', '.input-group',
      '.actions', '.header-actions', '.controls', '.interactive',
      '.clickable', '.dropdown', '.nav', '.navbar'
    ];
    
    interactiveSelectors.forEach(selector => {
      const elements = tableClone.querySelectorAll(selector);
      elements.forEach(element => element.remove());
    });
    
    // Remove any elements with interactive classes or attributes
    const allElements = tableClone.querySelectorAll('*');
    allElements.forEach(element => {
      // Remove elements with interactive classes
      const classList = element.className;
      if (typeof classList === 'string') {
        const interactiveClasses = ['btn', 'button', 'input', 'select', 'form', 'interactive', 'clickable', 'actions'];
        const hasInteractiveClass = interactiveClasses.some(cls => classList.includes(cls));
        if (hasInteractiveClass) {
          element.remove();
          return;
        }
      }
      
      // Remove elements with event handlers
      const hasEventHandlers = element.onclick || element.onchange || element.oninput || 
                              element.onmousedown || element.onmouseup || element.onkeydown;
      if (hasEventHandlers) {
        element.remove();
        return;
      }
      
      // Remove elements with interactive attributes
      const interactiveAttrs = ['onclick', 'onchange', 'oninput', 'onmousedown', 'onmouseup', 'onkeydown'];
      const hasInteractiveAttr = interactiveAttrs.some(attr => element.hasAttribute(attr));
      if (hasInteractiveAttr) {
        element.remove();
      }
    });
    
    const actionHeaders = tableClone.querySelectorAll('th.actions, td.actions, .action-column, th:last-child');
    actionHeaders.forEach(element => element.remove());
    const allRows = tableClone.querySelectorAll('tr');
    allRows.forEach(row => {
      const cells = row.querySelectorAll('td, th');
      if (cells.length > 0) {
        // Remove the last cell (Actions column)
        const lastCell = cells[cells.length - 1];
        if (lastCell.textContent.toLowerCase().includes('action') || 
            lastCell.classList.contains('actions')) {
          lastCell.remove();
        }
      }
    });

    // Create a new window for PDF generation
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Terminated Staff Records Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              background: white;
            }
            h1 { 
              text-align: center; 
              margin-bottom: 20px; 
              font-size: 18px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 0;
            }
            th, td { 
              border: 1px solid #000; 
              padding: 8px; 
              text-align: left;
              font-size: 12px;
            }
            th { 
              background-color: #f0f0f0; 
              font-weight: bold; 
              text-align: center;
            }
            button,
            input,
            select,
            textarea,
            .actions,
            .header-actions,
            .controls,
            .interactive,
            .clickable,
            .btn,
            .button,
            .form-control,
            .form-select,
            .input-group,
            .dropdown,
            .nav,
            .navbar,
            .no-print,
            [onclick],
            [onchange],
            [oninput],
            [onmousedown],
            [onmouseup],
            [onkeydown] { 
              display: none !important; 
              visibility: hidden !important;
            }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <h1>Terminated Staff Records Report</h1>
          ${tableClone.outerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <div className="terminated-staff-container">
      <div className="terminated-staff-header">
        <h2>Terminated Staff Management</h2>
         <div className="header-actions">
           <button 
             onClick={handlePrint}
             className="print-btn"
           >
             🖨️ Print
           </button>
           <button 
             onClick={handleDownload}
             className="download-btn"
           >
             📄 PDF
           </button>
         </div>
        {error && <div className="error-message">{error}</div>}
      </div>

      <div className="terminated-staff-content">
        <div className="staff-form-section">
          <h3>{editingId ? 'Edit Termination Record' : 'Add New Termination'}</h3>
          
          <div className="form-grid">
            <div className="form-group">
              <label>Company *</label>
              <select
                value={formData.company}
                onChange={(e) => handleInput('company', e.target.value)}
                required
                disabled={loading || editingId}
              >
                                 <option value="">Select Company</option>
                 {Array.isArray(allCompanies) && allCompanies.map(company => (
                   <option key={company._id || company.id || `company-${company.name}`} value={company._id || company.id}>{company.name}</option>
                 ))}
              </select>
            </div>

            <div className="form-group">
              <label>Staff Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInput('name', e.target.value)}
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label>Role/Position</label>
              <input
                type="text"
                value={formData.role}
                onChange={(e) => handleInput('role', e.target.value)}
                maxLength={50}
              />
            </div>

            <div className="form-group">
              <label>Department</label>
              <select
                value={formData.department}
                onChange={(e) => handleInput('department', e.target.value)}
              >
                                 <option value="">Select Department</option>
                 {Array.isArray(departments) && departments.map(dept => (
                   <option key={dept._id || dept.id || `dept-${dept.name}`} value={dept._id || dept.id}>{dept.name}</option>
                 ))}
              </select>
            </div>

            <div className="form-group">
              <label>Termination Date *</label>
              <input
                type="date"
                value={formData.terminationDate}
                onChange={(e) => handleInput('terminationDate', e.target.value)}
                required
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="form-group">
              <label>Last Working Day</label>
              <input
                type="date"
                value={formData.lastWorkingDay}
                onChange={(e) => handleInput('lastWorkingDay', e.target.value)}
                min={formData.terminationDate || undefined}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="form-group full-width">
              <label>Termination Reason *</label>
              <select
                value={formData.reason}
                onChange={(e) => handleInput('reason', e.target.value)}
                required
              >
                <option value="">Select Reason</option>
                <option value="resignation">Resignation</option>
                <option value="termination">Termination</option>
                <option value="retirement">Retirement</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group full-width">
              <label>Additional Details</label>
              <textarea
                value={formData.details}
                onChange={(e) => handleInput('details', e.target.value)}
                rows={3}
                maxLength={500}
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.noticePeriodServed}
                  onChange={(e) => handleInput('noticePeriodServed', e.target.checked)}
                />
                Notice Period Served
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={formData.exitInterview}
                  onChange={(e) => handleInput('exitInterview', e.target.checked)}
                />
                Exit Interview Conducted
              </label>
            </div>
          </div>

          <div className="form-actions">
            {editingId ? (
              <>
                <button className="hr-save-btn" onClick={() => handleUpdate(editingId)}>
                  <FaSave /> Save Changes
                </button>
                <button className="hr-cancel-btn" onClick={resetForm}>
                  <FaTimes /> Cancel
                </button>
              </>
            ) : (
              <button 
                className="add-btn"
                onClick={handleCreate}
                disabled={loading || !formData.company || !formData.terminationDate || !formData.reason}
              >
                <FaPlus /> {editingId ? 'Update Record' : 'Add Termination Record'}
              </button>
            )}
          </div>
        </div>

         <div className="staff-list-section">
           <h3>Terminated Staff Records</h3>
           
           
           {allCompanies.length === 0 && (
             <div className="info-message" style={{ 
               background: '#e3f2fd', 
               border: '1px solid #2196f3', 
               borderRadius: '4px', 
               padding: '8px 12px', 
               marginBottom: '16px',
               fontSize: '14px',
               color: '#1976d2'
             }}>
               ℹ️ Using hardcoded company and department data. API endpoints are currently unavailable.
             </div>
           )}
          {loading ? (
            <div className="loading">Loading records...</div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="records-table-container">
                <table className="records-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Department</th>
                      <th>Termination Date</th>
                      <th>Reason</th>
                      <th>Notice Served</th>
                      <th>Exit Interview</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {terminatedStaff.length > 0 ? (
                      terminatedStaff.map(staff => (
                        <tr key={staff._id || staff.id || `staff-${staff.name}-${staff.terminationDate}`}>
                          <td>
                            {staff.name}
                            {staff.isLocal && (
                              <span style={{ marginLeft: '5px', color: '#28a745' }} title="Stored locally">📱</span>
                            )}
                          </td>
                          <td>
                            <span className={`role-badge role-${staff.role?.toLowerCase().replace(' ', '-')}`}>
                              {staff.role}
                            </span>
                          </td>
                          <td>{getDepartmentName(staff.department)}</td>
                          <td>{formatTerminationDate(staff.terminationDate)}</td>
                          <td>{staff.reason}</td>
                          <td className={staff.noticePeriodServed ? 'status-present' : 'status-absent'}>
                            {staff.noticePeriodServed ? '✓ Yes' : '✗ No'}
                          </td>
                          <td className={staff.exitInterview ? 'status-present' : 'status-absent'}>
                            {staff.exitInterview ? '✓ Yes' : '✗ No'}
                          </td>
                          <td className="actions">
                            <button
                              className="edit-btn"
                              onClick={() => handleEdit(staff)}
                              disabled={loading || !(staff._id || staff.id)}
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() => handleDelete(staff._id || staff.id)}
                              disabled={loading || !(staff._id || staff.id)}
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="no-records">No terminated staff records found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="mobile-cards-container" style={{ display: 'none' }}>
                {terminatedStaff.length > 0 ? (
                  terminatedStaff.map(staff => (
                    <div key={staff._id || staff.id || `staff-${staff.name}-${staff.terminationDate}`} className="staff-card">
                      <div className="staff-card-header">
                        <h3 className="staff-card-name">
                          {staff.name}
                          {staff.isLocal && (
                            <span style={{ marginLeft: '5px', color: '#28a745' }} title="Stored locally">📱</span>
                          )}
                        </h3>
                        <span className={`staff-card-role role-${staff.role?.toLowerCase().replace(' ', '-')}`}>
                          {staff.role}
                        </span>
                      </div>
                      
                      <div className="staff-card-details">
                        <div className="staff-card-detail">
                          <span className="staff-card-label">Department</span>
                          <span className="staff-card-value">{getDepartmentName(staff.department)}</span>
                        </div>
                        <div className="staff-card-detail">
                          <span className="staff-card-label">Termination Date</span>
                          <span className="staff-card-value">{formatTerminationDate(staff.terminationDate)}</span>
                        </div>
                        <div className="staff-card-detail">
                          <span className="staff-card-label">Reason</span>
                          <span className="staff-card-value">{staff.reason}</span>
                        </div>
                      </div>
                      
                      <div className="staff-card-status">
                        <div className={`staff-card-status-item ${staff.noticePeriodServed ? 'status-present' : 'status-absent'}`}>
                          <span>{staff.noticePeriodServed ? '✓' : '✗'}</span>
                          <span>Notice Served: {staff.noticePeriodServed ? 'Yes' : 'No'}</span>
                        </div>
                        <div className={`staff-card-status-item ${staff.exitInterview ? 'status-present' : 'status-absent'}`}>
                          <span>{staff.exitInterview ? '✓' : '✗'}</span>
                          <span>Exit Interview: {staff.exitInterview ? 'Yes' : 'No'}</span>
                        </div>
                      </div>
                      
                      <div className="staff-card-actions">
                        <button
                          className="edit-btn"
                          onClick={() => handleEdit(staff)}
                          disabled={loading || !(staff._id || staff.id)}
                        >
                          <FaEdit /> Edit
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(staff._id || staff.id)}
                          disabled={loading || !(staff._id || staff.id)}
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-records">No terminated staff records found</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TerminatedStaffPage;